import { applyCardAnimationStyle } from "@theme/helpers";

const STORAGE_KEY = "viewedProducts";
const SECTION_SELECTOR = "[data-recently-viewed-section]";
const SLIDER_SELECTOR = "[data-recently-viewed-slider]";
const COLLECTION_SLIDER_SELECTOR = "[data-recently-viewed-collection-slider]";
const TRACK_SELECTOR = ".blaze-track";
const TAB_TRIGGER_SELECTOR = "[data-recently-viewed-tab-trigger]";
const TAB_PANEL_SELECTOR = "[data-recently-viewed-tab-panel]";
const TAB_NAV_SELECTOR = "[data-recently-viewed-tab-nav]";
const ARROWS_SELECTOR = ".blaze-navigation-arrows";
const ROOT_MARGIN = "500px";

const getStoredHandles = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Recently viewed storage unavailable", error);
    return null;
  }
};

const parseHandles = (value) => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((handle) => String(handle).trim()).filter(Boolean);
    }
  } catch (error) {
    // fall through to string parsing
  }

  return value
    .replace(/[\[\]]/g, "")
    .split(",")
    .map((handle) => handle.replace(/"/g, "").trim())
    .filter(Boolean);
};

const dedupe = (items) => {
  const seen = new Set();
  const result = [];

  items.forEach((item) => {
    if (seen.has(item)) {
      return;
    }
    seen.add(item);
    result.push(item);
  });

  return result;
};

const getBlazeController = async () => {
  if (window.themeBlazeSliderController) {
    return window.themeBlazeSliderController;
  }

  const { blazeSliderController } = await import("@theme/blaze-slider-controller");
  return blazeSliderController;
};

const initBlazeSlider = async (sliderEl) => {
  if (!sliderEl) {
    return null;
  }

  const controller = await getBlazeController();
  if (!controller) {
    return null;
  }

  // Blaze may have initialized while the track was still empty; rebuild with loaded slides.
  controller.destroy(sliderEl);
  return controller.initSlider(sliderEl);
};

const fetchProductCard = async (handle) => {
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
  const url = `${root}products/${encodeURIComponent(handle)}?section_id=product-card`;
  const response = await fetch(url, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Section render failed with status ${response.status}`);
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const productCard = doc.querySelector("product-card");

  if (!productCard) {
    return null;
  }

  const productId = productCard.getAttribute("data-product-id");

  if (productId === null || productId === "") {
    return null;
  }

  return productCard.outerHTML;
};

class RecentlyViewedSlider {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector(SLIDER_SELECTOR);
    this.track = this.slider?.querySelector(TRACK_SELECTOR) || null;
    this.maxProducts = Number(section.dataset.maxProducts || 12);
    this.hasCollectionTab = section.dataset.hasCollectionTab === "true";
    this.animationStyle = section.dataset.animationStyle || "none";

    this.tabTriggers = Array.from(section.querySelectorAll(TAB_TRIGGER_SELECTOR));
    this.tabPanels = Array.from(section.querySelectorAll(TAB_PANEL_SELECTOR));
    this.tabNavs = Array.from(section.querySelectorAll(TAB_NAV_SELECTOR));
    this.collectionSlider = section.querySelector(COLLECTION_SLIDER_SELECTOR);
    this.arrows = this.hasCollectionTab ? null : section.querySelector(ARROWS_SELECTOR);

    this.activeTab = "recently-viewed";
    this.collectionSliderInited = false;

    if (!this.slider || !this.track) {
      return;
    }

    if (this.hasCollectionTab) {
      this.bindTabs();
    }

    const stored = getStoredHandles();
    const handles = dedupe(parseHandles(stored)).slice(-this.maxProducts).reverse();

    if (!handles.length) {
      this.handleEmpty();
      return;
    }

    this.handles = handles;

    if (this.arrows) {
      this.arrows.hidden = true;
    }

    this.initObserver();
  }

  bindTabs() {
    this.tabTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const target = trigger.dataset.tabTarget;
        if (!target || target === this.activeTab) return;
        this.activateTab(target);
      });
    });
  }

  activateTab(tabId) {
    this.activeTab = tabId;

    this.tabTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.tabTarget === tabId;
      trigger.classList.toggle("active", isActive);
      trigger.classList.toggle("bold", isActive);
      trigger.classList.toggle("regular", !isActive);
      trigger.setAttribute("aria-selected", isActive ? "true" : "false");
      trigger.tabIndex = isActive ? 0 : -1;
    });

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.recentlyViewedTabPanel === tabId;
      panel.hidden = !isActive;
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.tabNavs.forEach((nav) => {
      const isActive = nav.dataset.recentlyViewedTabNav === tabId;
      nav.hidden = !isActive;
    });

    if (tabId === "collection" && !this.collectionSliderInited) {
      this.initCollectionSlider();
    }
  }

  initCollectionSlider() {
    if (!this.collectionSlider) return;

    const controller = window.themeBlazeSliderController;
    if (!controller || typeof controller.init !== "function") return;

    if (typeof controller.destroy === "function" && this.collectionSlider.__themeBlazeSlider) {
      controller.destroy(this.collectionSlider);
    }

    controller.init(this.collectionSlider);
    this.collectionSliderInited = true;
  }

  initObserver() {
    this.slider.setAttribute("hidden", "hidden");
    this.slider.setAttribute("aria-busy", "true");

    this.observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible) {
          return;
        }
        this.observer.disconnect();
        this.load();
      },
      {
        rootMargin: `${ROOT_MARGIN} 0px ${ROOT_MARGIN}`,
      },
    );

    this.observer.observe(this.section);
  }

  async load() {
    let appended = 0;
    const fragment = document.createDocumentFragment();

    for (const handle of this.handles) {
      try {
        const markup = await fetchProductCard(handle);
        if (!markup) {
          continue;
        }

        const slide = document.createElement("div");
        slide.classList.add("h-auto");
        slide.innerHTML = markup;
        applyCardAnimationStyle(slide, this.animationStyle);
        fragment.appendChild(slide);
        appended += 1;
      } catch (error) {
        console.warn(`Recently viewed product failed for handle "${handle}"`, error);
      }
    }

    if (appended > 0) {
      this.track.appendChild(fragment);
    }

    if (appended === 0) {
      this.handleEmpty();
      return;
    }

    this.slider.classList.remove("blaze-loading");
    this.slider.removeAttribute("hidden");
    this.slider.removeAttribute("aria-busy");

    if (this.arrows) {
      this.arrows.hidden = appended <= 1;
    }

    await initBlazeSlider(this.slider);
  }

  handleEmpty() {
    if (this.slider) {
      this.slider.classList.remove("blaze-loading");
      this.slider.setAttribute("hidden", "hidden");
      this.slider.removeAttribute("aria-busy");
    }

    if (this.arrows) {
      this.arrows.hidden = true;
    }

    // With a collection tab: hide the recently-viewed tab and switch to collection
    if (this.hasCollectionTab) {
      const rvTrigger = this.tabTriggers.find((t) => t.dataset.tabTarget === "recently-viewed");
      if (rvTrigger) rvTrigger.hidden = true;

      const rvPanel = this.tabPanels.find(
        (p) => p.dataset.recentlyViewedTabPanel === "recently-viewed",
      );
      if (rvPanel) {
        rvPanel.hidden = true;
        rvPanel.setAttribute("aria-hidden", "true");
      }

      const rvNav = this.tabNavs.find((n) => n.dataset.recentlyViewedTabNav === "recently-viewed");
      if (rvNav) rvNav.hidden = true;

      this.activateTab("collection");
      return;
    }

    this.section.hidden = true;
  }
}

const initRecentlyViewed = () => {
  const sections = document.querySelectorAll(SECTION_SELECTOR);
  sections.forEach((section) => new RecentlyViewedSlider(section));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecentlyViewed);
} else {
  initRecentlyViewed();
}
